/**
 * LocationCoordinateCheckCard
 *
 * Admin QA card that sweeps every geocoded `Locations` row looking for
 * coordinates stored with latitude/longitude TRANSPOSED, and lists them for
 * human review.
 *
 * Why it exists: a single row whose stored latitude is really a longitude
 * (e.g. -101.79) makes Mapbox throw `Invalid LngLat latitude value` and blanks
 * the entire Patient Migration map. Those rows are stale leftovers from before
 * the storage convention was finalized, so we expect FEW OR ZERO hits — a clean
 * run finding nothing is the normal, healthy outcome.
 *
 * The backend sorts each flagged row into a `category` (GET /v2/qa/location-coordinate-scan):
 *   error          - latitude outside -90…90. Mapbox throws. Highest priority.
 *   likely_swapped - impossible for US data AND swapping the values would land in
 *                    the US — a real transposition. Actionable → re-geocode.
 *   non_us         - impossible for US data but swapping still isn't US — a
 *                    legitimately non-US/territory row (e.g. Guam's positive
 *                    longitude). Informational only, NOT an error.
 * The header count reports only the actionable rows (error + likely_swapped);
 * non-US rows are shown for reference so a clean run reads as "0 to review".
 *
 * STRICTLY READ-ONLY. This card never writes, never re-geocodes, and never
 * touches the coordinate storage convention — it only surfaces candidates so a
 * human can repair them with the existing geocode/admin tooling.
 *
 * The backend is cursor-paged; this card loops pages until `nextAfterId` comes
 * back null so coverage is complete. If a scan ends early (stopped, errored, or
 * hit the page-count safety cap) it says so loudly rather than presenting
 * partial results as a full sweep.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import { format, isValid, parseISO } from 'date-fns';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Icon,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import fileDownload from 'js-file-download';
import axiosInstance from 'utils/axiosUtil';

// Rows examined per request. Smaller pages keep each request well inside the
// crud Lambda's timeout; the card loops pages either way.
const PAGE_SIZES = [1000, 5000, 10000, 25000];
const DEFAULT_PAGE_SIZE = 5000;

// Runaway guard. Not a coverage cap: on hitting it the scan stops and offers
// Resume, and the UI states plainly that the results are partial.
const MAX_PAGES = 500;

// Backend `category` values. `non_us` is informational — a legitimately non-US /
// territory row (e.g. Guam's positive longitude), NOT an error.
const CATEGORY_LABELS = {
  error: 'Crashes map',
  likely_swapped: 'Likely swapped',
  non_us: 'Non-US',
};

const isActionable = (s) => s.category !== 'non_us';

// Filters key off the backend `category` so "Likely swapped" never sweeps in the
// harmless non-US rows. `match` is also reused by filteredSuspects().
const FILTERS = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'error', label: 'Crashes map', match: (s) => s.category === 'error' },
  { key: 'likely_swapped', label: 'Likely swapped', match: (s) => s.category === 'likely_swapped' },
  { key: 'non_us', label: 'Non-US', match: (s) => s.category === 'non_us' },
];

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  cardInnerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardTitle: {
    flex: 1,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  blurb: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    maxWidth: 900,
  },
  readOnly: {
    fontWeight: 600,
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  pageSizeSelect: {
    minWidth: 150,
  },
  progress: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  progressText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  alert: {
    marginBottom: theme.spacing(2),
    fontSize: 13,
  },
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  summaryText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  filterToggle: {
    marginLeft: 'auto',
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      padding: '2px 12px',
      fontSize: 12,
    },
  },
  table: {
    marginTop: theme.spacing(1),
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: 'nowrap',
  },
  tableCell: {
    fontSize: 13,
    verticalAlign: 'middle',
  },
  coords: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  coordsBad: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'nowrap',
    color: '#c62828',
    fontWeight: 700,
  },
  coordsFixed: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'nowrap',
    color: '#2e7d32',
  },
  chipCrash: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 600,
    fontSize: 11,
    marginRight: 4,
  },
  chipSwapped: {
    backgroundColor: '#fff8e1',
    color: '#ef6c00',
    fontWeight: 600,
    fontSize: 11,
  },
  chipNonUs: {
    backgroundColor: '#eceff1',
    color: '#546e7a',
    fontWeight: 600,
    fontSize: 11,
  },
  coordsMuted: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  chipClean: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600,
    fontSize: 12,
  },
  subtle: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  clean: {
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: '#e8f5e9',
    borderRadius: 4,
  },
  cleanTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#2e7d32',
  },
  noResults: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtInt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

function fmtCoord(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return Number(n).toFixed(6);
}

function fmtDate(d) {
  if (!d) return '—';
  const parsed = d instanceof Date ? d : parseISO(d);
  return isValid(parsed) ? format(parsed, 'MM/dd/yyyy') : '—';
}

function fmtDateTime(d) {
  if (!d) return '—';
  const parsed = d instanceof Date ? d : parseISO(d);
  return isValid(parsed) ? format(parsed, 'MM/dd/yyyy h:mm a') : '—';
}

function csvCell(value) {
  if (value === null || value === undefined) return '""';
  return `"${String(value).replace(/"/g, '""')}"`;
}

// ── Component ─────────────────────────────────────────────────────────────────

class LocationCoordinateCheckCard extends Component {
  constructor(props) {
    super(props);
    this.stopRequested = false;
    this.state = {
      pageSize: DEFAULT_PAGE_SIZE,
      scanning: false,
      complete: false,       // true only when the cursor reached the end
      startedAt: null,
      finishedAt: null,
      scannedTotal: 0,
      totalWithPoint: null,  // from the first page; enables a real progress bar
      lastIdScanned: null,
      resumeAfterId: null,   // set when a run ends early, so it can be continued
      suspects: [],
      filter: 'all',
      error: null,
      warning: null,
      clampNoted: false,
    };
  }

  componentWillUnmount() {
    // Stop the page loop if the admin navigates away mid-scan.
    this.stopRequested = true;
  }

  requestStop = () => {
    this.stopRequested = true;
  };

  runScan = async ({ resume = false } = {}) => {
    const { pageSize } = this.state;
    let afterId = resume ? (this.state.resumeAfterId || 0) : 0;

    this.stopRequested = false;
    this.setState((prev) => ({
      scanning: true,
      complete: false,
      error: null,
      warning: null,
      clampNoted: false,
      resumeAfterId: null,
      startedAt: resume && prev.startedAt ? prev.startedAt : new Date(),
      finishedAt: null,
      suspects: resume ? prev.suspects : [],
      scannedTotal: resume ? prev.scannedTotal : 0,
      totalWithPoint: resume ? prev.totalWithPoint : null,
      lastIdScanned: resume ? prev.lastIdScanned : null,
    }));

    let pages = 0;

    try {
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const response = await axiosInstance.get('/v2/qa/location-coordinate-scan', {
          params: { afterId, scanLimit: pageSize },
        });
        const data = response.data || {};
        pages += 1;

        this.setState((prev) => ({
          scannedTotal: prev.scannedTotal + (data.scanned || 0),
          suspects: data.suspects && data.suspects.length
            ? prev.suspects.concat(data.suspects)
            : prev.suspects,
          totalWithPoint: data.totalWithPoint != null ? data.totalWithPoint : prev.totalWithPoint,
          lastIdScanned: data.lastIdScanned != null ? data.lastIdScanned : prev.lastIdScanned,
          // The server clamps oversized page requests — never hide that.
          clampNoted: prev.clampNoted || !!data.scanLimitClamped,
        }));

        if (data.nextAfterId == null) {
          this.setState({ scanning: false, complete: true, finishedAt: new Date() });
          return;
        }

        afterId = data.nextAfterId;

        if (this.stopRequested) {
          this.setState({
            scanning: false,
            finishedAt: new Date(),
            resumeAfterId: afterId,
            warning: `Scan stopped at location id ${afterId}. The results below cover only the rows scanned so far — press Resume for full coverage.`,
          });
          return;
        }

        if (pages >= MAX_PAGES) {
          this.setState({
            scanning: false,
            finishedAt: new Date(),
            resumeAfterId: afterId,
            warning: `Stopped after ${MAX_PAGES} pages (safety cap) at location id ${afterId}. This is NOT full coverage — press Resume to continue from there.`,
          });
          return;
        }
      }
    } catch (err) {
      this.setState({
        scanning: false,
        finishedAt: new Date(),
        resumeAfterId: afterId,
        error: err.response?.data?.error || err.message || 'Scan failed',
        warning: `The scan did not finish — results below are partial (stopped near location id ${afterId}). Press Resume to continue.`,
      });
    }
  };

  filteredSuspects() {
    const { suspects, filter } = this.state;
    const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];
    return suspects.filter(active.match);
  }

  downloadCsv = () => {
    const { suspects, complete } = this.state;
    if (suspects.length === 0) return;

    const headers = [
      'Location ID', 'Name', 'Organization ID', 'Organization', 'Address', 'City', 'State',
      'Postal Code', 'Country', 'Category', 'Stored Latitude', 'Stored Longitude', 'Crashes Map',
      'Likely Swapped', 'If Swapped Latitude', 'If Swapped Longitude', 'Swap Looks US',
      'Geocode Source', 'Geocode Date', 'Geocode Accuracy', 'Coverage',
    ];

    const rows = suspects.map((s) => [
      s.id,
      s.name || '',
      s.organization_id || '',
      s.organization_name || '',
      s.address1 || '',
      s.city || '',
      s.state || '',
      s.postal_code || '',
      s.country || '',
      CATEGORY_LABELS[s.category] || s.category || '',
      s.latitude,
      s.longitude,
      s.crashes_map ? 'Yes' : 'No',
      // Actionable interpretation, not the raw geometric flag: a non-US/territory
      // row also trips raw likely_swapped, but it is NOT a swap — so only rows
      // categorized as a real transposition say Yes here. See the Category column.
      s.category === 'likely_swapped' ? 'Yes' : 'No',
      s.swapped_latitude,
      s.swapped_longitude,
      s.swap_looks_us ? 'Yes' : 'No',
      s.geocode_source_name || s.geocode_source || '',
      s.geocode_date || '',
      s.geocode_accuracy || '',
      complete ? 'Full scan' : 'PARTIAL scan',
    ]);

    const csv = [
      headers.map(csvCell).join(','),
      ...rows.map((row) => row.map(csvCell).join(',')),
    ].join('\n');

    fileDownload(csv, `location-coordinate-qa-${format(new Date(), 'yyyyMMdd-HHmmss')}.csv`);
  };

  renderProgress() {
    const { classes } = this.props;
    const { scanning, scannedTotal, totalWithPoint, suspects } = this.state;
    if (!scanning) return null;

    const pct = totalWithPoint
      ? Math.min(100, Math.round((scannedTotal / totalWithPoint) * 100))
      : null;

    return (
      <div className={classes.progress}>
        <Typography className={classes.progressText}>
          {`Scanned ${fmtInt(scannedTotal)}`}
          {totalWithPoint ? ` of ${fmtInt(totalWithPoint)} geocoded locations` : ' locations'}
          {` — ${fmtInt(suspects.length)} suspect${suspects.length === 1 ? '' : 's'} so far`}
        </Typography>
        {pct === null ? (
          <LinearProgress />
        ) : (
          <LinearProgress variant='determinate' value={pct} />
        )}
      </div>
    );
  }

  renderSummary() {
    const { classes } = this.props;
    const {
      scanning, complete, suspects, scannedTotal, filter, finishedAt,
    } = this.state;
    if (scanning || !finishedAt) return null;

    const crashes = suspects.filter((s) => s.category === 'error').length;
    const realSwaps = suspects.filter((s) => s.category === 'likely_swapped').length;
    const actionable = crashes + realSwaps;
    const nonUs = suspects.length - actionable;

    return (
      <div className={classes.summary}>
        <Typography className={classes.summaryText}>
          {`${complete ? 'Full scan' : 'Partial scan'} — ${fmtInt(scannedTotal)} geocoded locations examined, `}
          {`${fmtInt(actionable)} to review (${fmtInt(crashes)} crash the map, ${fmtInt(realSwaps)} likely swapped)`}
          {nonUs > 0 ? ` · ${fmtInt(nonUs)} non-US/territory (not errors)` : ''}
          {` · ${fmtDateTime(finishedAt)}`}
        </Typography>
        {suspects.length > 0 && (
          <ToggleButtonGroup
            value={filter}
            exclusive
            size='small'
            className={classes.filterToggle}
            onChange={(event, next) => next && this.setState({ filter: next })}
          >
            {FILTERS.map((f) => (
              <ToggleButton key={f.key} value={f.key}>{f.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </div>
    );
  }

  renderTable() {
    const { classes } = this.props;
    const rows = this.filteredSuspects();

    if (rows.length === 0) {
      return (
        <Typography className={classes.noResults}>
          No rows match this filter.
        </Typography>
      );
    }

    return (
      <Table size='small' className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={classes.tableHeadCell}>ID</TableCell>
            <TableCell className={classes.tableHeadCell}>Location</TableCell>
            <TableCell className={classes.tableHeadCell}>Organization</TableCell>
            <TableCell className={classes.tableHeadCell}>City / State</TableCell>
            <TableCell className={classes.tableHeadCell}>Country</TableCell>
            <TableCell className={classes.tableHeadCell}>Stored lat, lng</TableCell>
            <TableCell className={classes.tableHeadCell}>If swapped</TableCell>
            <TableCell className={classes.tableHeadCell}>Flags</TableCell>
            <TableCell className={classes.tableHeadCell}>Geocoded</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((s) => (
            <TableRow key={s.id} hover>
              <TableCell className={classes.tableCell}>
                <Link to={`/locations/${s.id}`} target='_blank'>{s.id}</Link>
              </TableCell>
              <TableCell className={classes.tableCell}>
                {s.name || '—'}
                {s.address1 && (
                  <div className={classes.subtle}>{s.address1}</div>
                )}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {s.organization_id ? (
                  <Link to={`/organizations/${s.organization_id}`} target='_blank'>
                    {s.organization_name || `#${s.organization_id}`}
                  </Link>
                ) : '—'}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {[s.city, s.state].filter(Boolean).join(', ') || '—'}
                {s.postal_code && <div className={classes.subtle}>{s.postal_code}</div>}
              </TableCell>
              <TableCell className={classes.tableCell}>{s.country || '—'}</TableCell>
              <TableCell className={classes.tableCell}>
                <span className={s.crashes_map ? classes.coordsBad : classes.coords}>
                  {`${fmtCoord(s.latitude)}, ${fmtCoord(s.longitude)}`}
                </span>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <span className={s.swap_looks_us ? classes.coordsFixed : classes.coordsMuted}>
                  {`${fmtCoord(s.swapped_latitude)}, ${fmtCoord(s.swapped_longitude)}`}
                </span>
                <div className={classes.subtle}>
                  {s.swap_looks_us
                    ? 'lands in the US — transposition likely'
                    : 'still non-US when swapped — legitimately outside the US'}
                </div>
              </TableCell>
              <TableCell className={classes.tableCell}>
                {s.category === 'error' && (
                  <Tooltip title='Latitude is outside -90…90 — Mapbox throws and the map goes blank'>
                    <Chip size='small' label='Crashes map' className={classes.chipCrash} />
                  </Tooltip>
                )}
                {s.category === 'likely_swapped' && (
                  <Tooltip title='Impossible for US data and swapping would land in the US — likely a real transposition'>
                    <Chip size='small' label='Likely swapped' className={classes.chipSwapped} />
                  </Tooltip>
                )}
                {s.category === 'non_us' && (
                  <Tooltip title='Legitimately outside the US (e.g. a territory like Guam) — flagged for reference, not an error'>
                    <Chip size='small' label='Non-US' className={classes.chipNonUs} />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {fmtDate(s.geocode_date)}
                <div className={classes.subtle}>
                  {s.geocode_source_name || (s.geocode_source != null ? `source ${s.geocode_source}` : 'source unknown')}
                  {s.geocode_accuracy ? ` · ${s.geocode_accuracy}` : ''}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  render() {
    const { classes, user } = this.props;
    const {
      pageSize, scanning, complete, suspects, scannedTotal,
      error, warning, clampNoted, resumeAfterId, finishedAt,
    } = this.state;

    // Route and endpoint are both admin-gated; this is belt-and-braces.
    if (!user?.is_admin) {
      return (
        <Card className={classes.card} elevation={2}>
          <CardContent>
            <Typography className={classes.noResults}>
              Location QA is available to administrators only.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const finishedScan = !scanning && complete && !!finishedAt;
    const actionableCount = suspects.filter(isActionable).length;
    const nonUsCount = suspects.length - actionableCount;
    // Truly nothing flagged.
    const finishedClean = finishedScan && suspects.length === 0;
    // No real problems, but some legit non-US rows were flagged — reassure, don't alarm.
    const finishedNoErrors = finishedScan && actionableCount === 0 && nonUsCount > 0;

    return (
      <Card className={classes.card} elevation={2}>
        <CardContent>
          <div className={classes.cardInnerHeader}>
            <Typography component='h2' variant='h6' color='primary' gutterBottom className={classes.cardTitle}>
              Location Coordinates — Swapped Lat/Lng Finder
            </Typography>
            <div className={classes.actions}>
              {scanning ? (
                <Button
                  color='primary'
                  size='small'
                  onClick={this.requestStop}
                  startIcon={<CircularProgress size={16} color='inherit' />}
                >
                  Stop Scan
                </Button>
              ) : (
                <Button
                  color='primary'
                  size='small'
                  onClick={() => this.runScan()}
                  startIcon={<Icon fontSize='small'>travel_explore</Icon>}
                >
                  {finishedAt ? 'Re-run Scan' : 'Run Scan'}
                </Button>
              )}
              {!scanning && resumeAfterId != null && (
                <Button
                  color='primary'
                  size='small'
                  onClick={() => this.runScan({ resume: true })}
                  startIcon={<Icon fontSize='small'>play_arrow</Icon>}
                >
                  Resume
                </Button>
              )}
              <Button
                size='small'
                onClick={this.downloadCsv}
                disabled={scanning || suspects.length === 0}
                startIcon={<Icon fontSize='small'>download</Icon>}
              >
                Download CSV
              </Button>
            </div>
          </div>

          <Typography className={classes.blurb}>
            Sweeps every geocoded location and flags rows whose latitude/longitude look
            transposed. A latitude outside −90…90 makes Mapbox throw and blanks the whole
            Patient Migration map; the softer flag catches transpositions that only misplace
            a marker. Rows that are simply outside the US (e.g. Guam and other territories) are
            separated out as “non-US” and do not count as errors.
            <span className={classes.readOnly}> This scan is read-only</span> — it
            never re-geocodes or edits a row. Review a flagged location, then repair it with
            the existing geocode tooling. Finding nothing is the expected result.
          </Typography>

          <div className={classes.controls}>
            <FormControl size='small' className={classes.pageSizeSelect}>
              <InputLabel>Rows per request</InputLabel>
              <Select
                value={pageSize}
                label='Rows per request'
                disabled={scanning}
                onChange={(e) => this.setState({ pageSize: e.target.value })}
              >
                {PAGE_SIZES.map((size) => (
                  <MenuItem key={size} value={size}>{fmtInt(size)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography className={classes.summaryText}>
              The scan pages through the whole table by id; lower this if a request times out.
            </Typography>
          </div>

          {this.renderProgress()}

          {error && (
            <Alert severity='error' className={classes.alert}>{error}</Alert>
          )}
          {warning && (
            <Alert severity='warning' className={classes.alert}>{warning}</Alert>
          )}
          {clampNoted && (
            <Alert severity='info' className={classes.alert}>
              Requested page size exceeded the server maximum and was reduced. Coverage is
              unaffected — the scan simply used more pages.
            </Alert>
          )}

          {this.renderSummary()}

          {finishedClean && (
            <div className={classes.clean}>
              <Typography className={classes.cleanTitle}>
                No coordinate anomalies found
              </Typography>
              <Typography className={classes.summaryText}>
                {`All ${fmtInt(scannedTotal)} geocoded locations have plausible coordinates.`}
              </Typography>
            </div>
          )}

          {finishedNoErrors && (
            <Alert severity='info' className={classes.alert}>
              {`No coordinate errors found. ${fmtInt(nonUsCount)} `}
              {`non-US/territory location${nonUsCount === 1 ? '' : 's'} `}
              {'(e.g. Guam, the Northern Marianas) are flagged below for reference — their '}
              {'coordinates are legitimately outside the US and are not errors.'}
            </Alert>
          )}

          {!scanning && suspects.length > 0 && this.renderTable()}

          {!scanning && !finishedAt && (
            <Typography className={classes.noResults}>
              Press Run Scan to sweep every geocoded location for transposed coordinates.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }
}

LocationCoordinateCheckCard.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object,
};

function mapStateToProps(state) {
  return { user: state.user || {} };
}

const styled = withStyles(styles)(LocationCoordinateCheckCard);
const connected = connect(mapStateToProps)(styled);
export { connected as LocationCoordinateCheckCard };
export default connected;
